import React, { useState } from "react";

import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import { Divider, Skeleton, Typography } from "@mui/material";

import Button from "@readai/common/components/Button";
import InfoPanel from "@readai/common/components/InfoPanel";
import Link from "@readai/common/components/Link";
import { Italic } from "@readai/common/components/styles";
import usePageTitle from "@readai/common/hooks/usePageTitle";
import { getTimezoneName } from "@readai/common/utils";
import { withQueryParams } from "@readai/common/utils";
import { formatStartAndEndTime } from "@readai/common/utils/datetimeUtils";
import ChevronLeft from "@readai/icons/uui/ChevronLeft";
import ChevronRight from "@readai/icons/uui/ChevronRight";
import Stack, { Row } from "@readai/pulse/components/Stack";

import ReadSmartSchedulerLogo from "@app/booking/components/ReadSmartSchedulerLogo";
import useRssLinkCalendarEvent from "@app/booking/hooks/useRssLinkCalendarEvent";
import ROUTES from "@app/booking/routes";
import useSchedulingStore from "@app/booking/stores/scheduling";

import { MoreDetails } from "./styles";

const timezoneAwareDate = (date: DateTime, timezone: string | null) => {
  return timezone ? date.setZone(timezone) : date;
};

const EventDetails = () => {
  usePageTitle(t`Event`, {
    eventTitle: "Event",
  });

  const { i18n } = useLingui();
  const [queryParams] = useSearchParams();
  const { eventId } = useParams();
  const [showDetails, setShowDetails] = useState(false);

  const action = queryParams.get("action");
  const isRescheduling = action === "reschedule";
  const isSuccess = queryParams.get("success") === "true";

  const selectedTimezone = useSchedulingStore(
    (state) => state.selectedTimezone,
  );

  const {
    getEvent: { data: event },
  } = useRssLinkCalendarEvent();

  const hasAdditionalDetails =
    !isEmpty(event?.details) || !isEmpty(event?.additionalGuestsEmails);

  const rescheduleLink = event?.rssLinkVanityId
    ? withQueryParams(
        ROUTES.scheduleByVanityIds(
          event.rssUserVanityId!,
          event.rssLinkVanityId,
        ),
        { eventId },
      )
    : undefined;

  const timezoneAwareStartTime = event
    ? timezoneAwareDate(event.startTime, selectedTimezone)
    : null;
  const timezoneAwareEndTime = event
    ? timezoneAwareDate(event.endTime, selectedTimezone)
    : null;

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={2.5}
      sx={{ width: showDetails ? "900px" : "500px", maxWidth: "100%" }}
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        <ReadSmartSchedulerLogo />
        {event && isSuccess && (
          <InfoPanel severity="success">
            {isRescheduling ? (
              <Trans>Your meeting was successfully rescheduled!</Trans>
            ) : (
              <Trans>Your meeting was successfully scheduled!</Trans>
            )}
          </InfoPanel>
        )}
        {event ? (
          <Typography variant="h2" fontWeight={500}>
            {event.title}
          </Typography>
        ) : (
          <Skeleton variant="text" />
        )}
        <Divider />
        <Stack spacing={0.5} sx={{ color: "text.default", pb: 1.5 }}>
          {timezoneAwareStartTime ? (
            <>
              <Typography variant="bodyPrimaryMed">
                {timezoneAwareStartTime.toLocaleString(DateTime.DATE_HUGE)}
              </Typography>
              <Typography variant="bodyPrimaryMed">
                {formatStartAndEndTime(
                  timezoneAwareStartTime,
                  timezoneAwareEndTime,
                  { locale: i18n.locale },
                )}
              </Typography>
              <Typography variant="bodySecondaryReg" color="text.weak">
                {timezoneAwareStartTime.toFormat("ZZZZZ")}{" "}
                {timezoneAwareStartTime.zone.name
                  ? `- ${getTimezoneName(timezoneAwareStartTime.zone.name)}`
                  : ""}
              </Typography>
            </>
          ) : (
            <>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </>
          )}
        </Stack>
        {hasAdditionalDetails && (
          <Row>
            <Button
              endIcon={showDetails ? <ChevronLeft /> : <ChevronRight />}
              variant="outlined"
              onClick={() => setShowDetails(!showDetails)}
              sx={{ fontSize: "1rem" }}
            >
              {showDetails ? (
                <Trans>Hide event details</Trans>
              ) : (
                <Trans>Show event details</Trans>
              )}
            </Button>
          </Row>
        )}
        <Divider />
        <Row spacing={1} flexWrap="wrap" variant="left">
          <Typography variant="bodySecondaryReg" color="text.weak">
            <Trans>More options:</Trans>
          </Typography>
          {rescheduleLink && (
            <Link variant="bodySecondaryMed" underline to={rescheduleLink}>
              <Trans>Reschedule Meeting</Trans>
            </Link>
          )}
          <Divider orientation="vertical" sx={{ height: "11px" }} />
          <Link
            variant="bodySecondaryMed"
            to={ROUTES.cancelEventById(eventId!)}
            underline
            color="error.main"
          >
            <Trans>Cancel Meeting</Trans>
          </Link>
        </Row>
      </Stack>
      {showDetails && (
        <MoreDetails spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryMed" fontWeight={600}>
              <Trans>Additional guests</Trans>
            </Typography>
            <Typography variant="bodySecondaryReg">
              {isEmpty(event?.additionalGuestsEmails) ? (
                <Italic sx={{ color: "shades.neutral.400" }}>
                  <Trans>No additional guests</Trans>
                </Italic>
              ) : (
                (event?.additionalGuestsEmails ?? []).map((email, i, arr) => (
                  <>
                    <Link
                      key={email}
                      variant="bodySecondaryReg"
                      href={`mailto:${email}`}
                    >
                      {email}
                    </Link>
                    {i < arr.length - 1 && ", "}
                  </>
                ))
              )}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryMed" fontWeight={600}>
              <Trans>Additional details</Trans>
            </Typography>
            <Typography variant="bodySecondaryReg">
              {isEmpty(event?.details) ? (
                <Italic sx={{ color: "text.weak" }}>
                  <Trans>No additional details</Trans>
                </Italic>
              ) : (
                event?.details
              )}
            </Typography>
          </Stack>
        </MoreDetails>
      )}
    </Stack>
  );
};

export default EventDetails;
